import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import Chart, {ActiveElement, ChartEvent, TooltipItem} from "chart.js/auto";
import {LineChartData} from "../../../interfaces/line-chart-data";
import {UiHelperService} from "../../../services/ui-helper.service";
import {Scale} from "chart.js";
import 'chartjs-adapter-moment';

@Component({
  selector: 'brt-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss']
})
export class LineChartComponent implements AfterViewInit {
  @ViewChild("chartEl") chartEl!: ElementRef;
  @Output('onIndexHover') onIndexHover = new EventEmitter<number>();

  @Input('chartTarget') chartTarget = "";
  @Input('y-label') yLabel = "";
  @Input('data') set setData(data: LineChartData) {
    // console.log("Data updated");
    this.data = data;

    if(this.isFirstSetData) {
      this.isFirstSetData = false;
    } else {
      if(this.chart) {
        this.chart.data.datasets = [];
        this.chart.data.labels = [];

        this.chart.data.datasets = this.data.datasets.map(row => {
          return {
            label: '',
            data: row.items.map((item) => item.value),
            backgroundColor: row.color,
            borderColor: row.color,
            borderWidth: 4,
            tension: 0.2
          }
        })

        this.chart.data.labels = this.data.datasets[0].items.map(item => item.label);

        this.chart.update();
        // console.log("chart updated");
      }
    }

  }

  data!: LineChartData;
  isFirstSetData = true;
  currentSelection = -1;

  chart!: Chart;

  constructor(private uiService: UiHelperService) {
  }

  ngAfterViewInit(): void {
    if(this.data) {

      // console.log('line-chart-data',this.data);
      Chart.defaults.font.family = "Inter";
      const chartEl = this.chartEl.nativeElement as HTMLCanvasElement;
      chartEl.height = 250;

      const labelsMap = new Map<string, void>();
      this.data.datasets.forEach(data => {
        data.items.forEach(item => {
          labelsMap.set(item.label, undefined);
        })
      });

      let labels: string[] = [];
      labelsMap.forEach((label, key) => {
        labels.push(key)
      });

      labels = labels.sort((a,b) => {
        const aInt = parseInt(a);
        const bInt = parseInt(b);
        return aInt > bInt ? 1 : -1;
      });

      this.chart = new Chart(
        chartEl,
        {
          type: 'line',
          options: {
            spanGaps: true,
            layout: {
              padding: {
                top: 0,
                left: 0,
                bottom: 0,
                right: 0
              }
            },
            maintainAspectRatio: false,
            elements: {
              point: {
                radius: 0.1 // 0
              }
            },
            scales: {
              y: {
                afterUpdate(axis: Scale) {
                  axis.paddingTop = 0;
                  axis.paddingRight = 0;
                  axis.paddingLeft = 0;
                  axis.paddingBottom = 0;
                },
                beginAtZero: true,
                border: {
                  display: false
                },
                offset: true,
                grid: {
                  color: "rgba(255, 255, 255, 0.20)"
                },
                ticks: {
                  callback: function(value, index, ticks) {
                    return value.toLocaleString('de-DE'); // German locale uses period as thousands separator
                  },
                  crossAlign: "center",
                  padding: this.uiService.getScaledPixel(8),
                  color: "rgba(255, 255, 255, 0.70)",
                  maxTicksLimit: 4,
                  font: {
                    size: this.uiService.getScaledPixel(12),
                    family: "Inter",
                    weight: 500
                  }
                }
              },
              x: {
                afterBuildTicks(axis: Scale) {
                  // console.log('line-chart-axis',axis);
                },
                afterUpdate(axis: Scale) {
                  axis.paddingTop = 0;
                  axis.paddingRight = 0;
                  axis.paddingLeft = 0;
                  axis.paddingBottom = 0;
                },
                // offset: true,
                border: {
                  display: false
                },
                grid: {
                  display: false
                },
                ticks: {
                  source: 'data',
                  align: "inner",
                  color: "rgba(255, 255, 255, 0.70)",
                  font: {
                    size: this.uiService.getScaledPixel(12),
                    family: "Inter",
                    weight: 500
                  }
                },
                type: "time",
                time: {
                  // unit: "month",
                  minUnit: "day",
                  displayFormats: {
                    day: "DD.MM",
                    week: "DD.MM",
                    month: "MMMM",
                    year: "YYYY"
                  }
                }
              }
            },

            // events: ["mousemove"],
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                yAlign: "bottom",
                bodyAlign: "center",
                enabled: true,
                intersect: false,
                mode: 'nearest',
                bodyColor: "#353879",
                displayColors: false,
                backgroundColor: "#ffffff",
                padding: this.uiService.getScaledPixel(12),
                cornerRadius: this.uiService.getScaledPixel(12),
                caretSize: this.uiService.getScaledPixel(12),
                borderColor: "#ffffff",
                bodyFont: {
                  family: "Inter",
                  size: this.uiService.getScaledPixel(12),
                  weight: 500
                },
                callbacks: {
                  label: (item): string => {
                    let value = item.raw as string;
                    let valueNumber = item.raw as number;
                    let valueStr = "";
                    switch (this.chartTarget) {
                      case "points":
                        valueStr = parseInt(value).toLocaleString("DE-de");
                        valueStr =  valueStr + ' Points';
                        break;
                      case "playTime":
                        console.log(item.raw);
                        const hours = Math.floor(valueNumber);
                        const minutes = (valueNumber % 1 * 60).toFixed(0);
                        valueStr =  `${hours} Std. ${minutes} Min.`;
                        break;
                      default:
                        if(this.chartTarget.length) {
                          console.warn("No handler for chart target " + this.chartTarget + " was found!");
                        }
                    }

                    return valueStr;


                  },
                  title: function () {
                    return "";
                  }
                }
              }
            },
            hover: {
              mode: 'nearest',
              intersect: false,
            }
          },
          data: {
            labels: labels,
            datasets: this.data.datasets.map(row => {

              const data: any[] = [];
              labels.forEach((label) => {
                let valueIdx = row.items.findIndex((el) => el.label === label);
                if(valueIdx === -1) {
                  data.push(null);
                } else {
                  data.push(row.items[valueIdx].value);
                }
              })
              return {
                label: '',
                data: data,
                backgroundColor: row.color,
                borderColor: row.color,
                borderWidth: 4,
                tension: 0.2,
                showLine: true
              }
            })
          }
        }
      );
    }
  }
}
